/**
 * Data Catalog Helper Functions
 * Pure utility functions without state dependencies
 */

import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ScienceIcon from '@mui/icons-material/Science';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AirIcon from '@mui/icons-material/Air';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RadarIcon from '@mui/icons-material/Radar';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import BiotechIcon from '@mui/icons-material/Biotech';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

/**
 * Tag icon options for category customization
 */
export const tagIconOptions = [
  { name: 'MonitorHeart', icon: MonitorHeartIcon, label: 'Vitalparameter' },
  { name: 'Science', icon: ScienceIcon, label: 'Labor' },
  { name: 'MedicalServices', icon: MedicalServicesIcon, label: 'Klinisch' },
  { name: 'Air', icon: AirIcon, label: 'Beatmung' },
  { name: 'Favorite', icon: FavoriteIcon, label: 'Kardiologie' },
  { name: 'Psychology', icon: PsychologyIcon, label: 'Neurologie' },
  { name: 'Radar', icon: RadarIcon, label: 'Monitoring' },
  { name: 'Vaccines', icon: VaccinesIcon, label: 'Medikation' },
  { name: 'Biotech', icon: BiotechIcon, label: 'Mikrobiologie' },
  { name: 'Bloodtype', icon: BloodtypeIcon, label: 'BGA' },
  { name: 'Timeline', icon: TimelineIcon, label: 'Zeitreihe' },
  { name: 'Assessment', icon: AssessmentIcon, label: 'Score' },
  { name: 'CameraAlt', icon: CameraAltIcon, label: 'Bildgebung' },
  { name: 'Coronavirus', icon: CoronavirusIcon, label: 'COVID-19' },
  { name: 'LocalHospital', icon: LocalHospitalIcon, label: 'Station' },
  { name: 'LocalOffer', icon: LocalOfferIcon, label: 'Allgemein' },
];

/**
 * Get icon component by name
 * @param {string} iconName - Name of the icon
 * @returns {React.Component|null} Icon component or null
 */
export const getIconComponent = (iconName) => {
  const iconOption = tagIconOptions.find(opt => opt.name === iconName);
  return iconOption ? iconOption.icon : null;
};

/**
 * Get OMOP status color for MUI Chip
 * @param {string} status - OMOP mapping status ('validated', 'mapped', 'pending')
 * @returns {string} MUI color name
 */
export const getOMOPStatusColor = (status) => {
  switch (status) {
    case 'validated':
      return 'success';
    case 'mapped':
      return 'info';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * Get OMOP status label in German
 * @param {string} status - OMOP mapping status
 * @returns {string} German label
 */
export const getOMOPStatusLabel = (status, t) => {
  const translate = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);

  switch (status) {
    case 'validated':
      return translate('dataCatalog.omopStatus.validated', 'Validiert');
    case 'mapped':
      return translate('dataCatalog.omopStatus.mapped', 'Gemappt');
    case 'pending':
      return translate('dataCatalog.omopStatus.pending', 'Ausstehend');
    case 'suggested':
      return translate('dataCatalog.omopStatus.suggested', 'Vorgeschlagen');
    case 'not_mapped':
      return translate('dataCatalog.omopStatus.notMapped', 'Nicht gemappt');
    default:
      return translate('dataCatalog.omopStatus.unknown', 'Unbekannt');
  }
};

/**
 * Get primary name based on view mode
 * @param {Object} param - Parameter object
 * @param {string} nameView - View mode ('charite', 'omop', 'both')
 * @returns {string} Primary name
 */
export const getPrimaryName = (param, nameView) => {
  if (nameView === 'omop') return param.omopName || param.name;
  return param.name;
};

/**
 * Get secondary name for dual view mode
 * @param {Object} param - Parameter object
 * @param {string} nameView - View mode
 * @returns {string|null} Secondary name or null
 */
export const getSecondaryName = (param, nameView) => {
  if (nameView === 'both') {
    return param.omopName;
  }
  return null;
};

/**
 * Get primary description based on view mode
 * @param {Object} param - Parameter object
 * @param {string} nameView - View mode
 * @returns {string} Primary description
 */
export const getPrimaryDescription = (param, nameView) => {
  if (nameView === 'omop') return param.omopDescription || param.description;
  return param.description;
};
